import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  SkipSelf,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlContainer } from '@angular/forms';
import {
  BehaviorSubject,
  concat,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

// SERVICES
import {
  PurchaseOrderRelationsConstant,
  EstimateRelationsConstant,
  InvoiceRelationsConstant,
} from '@app/core/_constant/relations.constants';
import { KitService } from '@app/core/kits/services/kit.service';
import { RfqService } from '@app/core/quotes';

@Component({
  selector: 'cnc-line-items-multiselect',
  templateUrl: './line-items-multiselect.component.html',
  styleUrls: ['./line-items-multiselect.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (container :ControlContainer) => container,
      deps: [[new SkipSelf(), ControlContainer]],
    },
  ],
})
export class LineItemsMultiselectComponent implements OnInit, OnDestroy {
  @Input()
  isKits :boolean = false;

  @Input()
  showImage :boolean = true;

  @Input()
  showDescription :boolean = true;

  @Input()
  disabled :boolean;

  @Input()
  excludeKits :boolean = false;

  @Input()
  skusOnly :boolean = false;

  @Input()
  rfqType :string = '';

  @Input()
  warehouseGroupId :number = 0;

  @Input()
  ftInvoiceWarehouseGroup :boolean = false;

  @Input()
  supplierId :number;

  @Input()
  warehouseId :number;

  @Output()
  selectItemsEvent = new EventEmitter<any>();

  @Output()
  resetLineItemEvent = new EventEmitter<any>();

  item$ :Observable<any[]>;
  loading = false;
  itemInput$ = new Subject<string>();

  selected :any[] = [];
  selectedIds :any[] = [];
  selectedItems :any[] = [];

  hasSelectedItems :boolean = false;

  private readonly _items = new BehaviorSubject<any>(<any>[]);
  public items$ = this._items.asObservable();

  @Input()
  forPoForm :boolean = false;

  @Input()
  forInvoiceForm :boolean = false;

  @Input()
  forEstimateForm :boolean = false;

  items :any = [];
  itemsBuffer :any = [];
  bufferSize :number = 15;
  numberOfItemsFromEndBeforeFetchingMore :number = 15;
  searchTerm :string = '';
  offsetCount :number = 0;
  private searchSku :Subscription;

  constructor(
    private rfqSvc :RfqService,
    private kitSvc :KitService,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.loadItems();
  }

  ngOnDestroy() :void {
    this._items.unsubscribe();
    if (this.searchSku) {
      this.searchSku.unsubscribe();
    }
  }

  searchFn(term :string, item :any) :any {
    term = term.toLocaleLowerCase();

    return (
      item.sku.toLocaleLowerCase().indexOf(term) > -1 ||
      item.product_name.toLocaleLowerCase().indexOf(term) > -1
    );
  }

  changeImageSourceOnError(event :any) :void {
    event.target.src = 'assets/media/no_image_placeholder.jpg';
  }

  onItemsSelect(item :any) :void {
    if (!this.selectedIds.includes(item.id)) {
      this.hasSelectedItems = true;
      this.selected.push(item);
      this.selectedIds.push(item.id);
      this.selectItemsEvent.emit({ item });
    }
  }

  trackByFn(item :any) :any {
    return item.id;
  }

  resetSelectedItems() :void {
    // reset
    this.selected = [];
    this.selectedIds = [];
    this.selectedItems = [];
  }

  private loadItems() :void {
    this.item$ = concat(
      of([]),
      this.itemInput$.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => (this.loading = true)),
        switchMap((term) => {
          this.searchTerm = term;

          return this.isKits ? this.getKits(term) : this.getRfqs(term);
        }),
      ),
    );

    this.item$.subscribe((data) => {
      this.items = data;
      this.itemsBuffer = this.items.slice(0, this.bufferSize);
      this._items.next(data);
      this.offsetCount += 1;
      this.cd.detectChanges();
    });
  }

  setParams(term :string = '', offset :number = 0) :any {
    const params :any = {
      sku: term,
    };

    if (this.supplierId && this.forPoForm) {
      params.supplierId = this.supplierId;
      params.warehouseId = this.warehouseId;
    }

    if (this.rfqType !== '') {
      params.rfqType = this.rfqType;
    }

    if (!this.isKits) {
      if (this.excludeKits) {
        params.noKits = true;
      }
      if (this.forPoForm) {
        params.relations = PurchaseOrderRelationsConstant.join(',');
      }
      if (this.forEstimateForm) {
        params.relations = EstimateRelationsConstant.join(',');
      }
      if (this.forInvoiceForm) {
        if (this.ftInvoiceWarehouseGroup) {
          params.warehouse_group_id = this.warehouseGroupId;
          InvoiceRelationsConstant.push('locationStock');
        }
        params.relations = InvoiceRelationsConstant.join(',');
      }

      params.isMultiSelect = true;
      params.per_page = this.bufferSize;
      params.offset = offset;
    }

    return params;
  }

  private getRfqs(term :string) :Observable<any> {
    this.offsetCount = 0;
    const params = this.setParams(term, this.offsetCount);

    return this.rfqSvc.searchSKUDropdown(params).pipe(
      catchError(() => of([])), // empty list on error
      tap(() => (this.loading = false)),
      map((resp :any) => resp),
    );
  }

  private getKits(term :string) :Observable<any> {
    this.offsetCount = 0;

    return this.kitSvc.searchKitsDropdown({ sku: term }).pipe(
      catchError(() => of([])), // empty list on error
      tap(() => (this.loading = false)),
      map((resp) => resp),
    );
  }

  onScrollToEnd() :void {
    this.fetchMore();
  }

  onScroll({ end }) :void {
    if (this.loading || this.items.length <= this.itemsBuffer.length) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      this.itemsBuffer.length
    ) {
      this.fetchMore();
    }
  }

  private fetchMore() :void {
    this.loading = true;
    const params = this.setParams(
      this.searchTerm,
      this.offsetCount * this.bufferSize,
    );
    if (this.isKits) {
      this.searchSku = this.kitSvc.searchKitsDropdown(params).subscribe((resp) => {
        this.loading = false;
        this.itemsBuffer = this.itemsBuffer.concat(resp);
        this.offsetCount += 1;
        this.cd.detectChanges();
      });
    } else {
      this.searchSku = this.rfqSvc.searchSKUDropdown(params).subscribe((resp) => {
        this.loading = false;
        this.itemsBuffer = this.itemsBuffer.concat(resp);
        this.offsetCount += 1;
        this.cd.detectChanges();
      });
    }
  }
}
